import { HttpClient, HttpHeaders } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { Observable } from "rxjs";
import { Language } from "../models/language";
import {
  CloneSurvey,
  SaveReviewResponse,
  SaveAdministrativeUnitSurveyMessage,
  Survey,
  SurveyAnswerModel,
  TenantSurvey,
  AdministrativeUnitSurveyMessage,
  TranslateSurvey,
} from "../models/survey";

@Injectable({
  providedIn: "root",
})
export class SurveyService {
  private baseUrl = "api/surveys";

  constructor(private http: HttpClient) {}

  getAll() {
    return this.http.get<Survey[]>(`${this.baseUrl}`);
  }

  get(surveyId: string, languageId: string, administrativeUnitId: string = null) {
    if (administrativeUnitId) {
      return this.http.get<Survey>(
        `${this.baseUrl}/${surveyId}/${languageId}/${administrativeUnitId}`
      );
    }
    return this.http.get<Survey>(`${this.baseUrl}/${surveyId}/${languageId}`);
  }

  getByAdministrativeUnitSurveyId(
    surveyId: string,
    languageId: string,
    administrativeUnitId: string,
    administrativeUnitSurveyId: string
  ) {
    return this.http.get<Survey>(
      `${this.baseUrl}/${surveyId}/${languageId}/${administrativeUnitId}/${administrativeUnitSurveyId}`
    );
  }

  getByAdministrativeUnitSurveyIdStateAndAu(
    surveyId: string,
    languageId: string,
    administrativeUnitId: string,
    administrativeUnitSurveyId: string
  ) {
    return this.http.get<Survey>(
      `${this.baseUrl}/getsurveybytenantsurveystateandau/${surveyId}/${languageId}/${administrativeUnitId}/${administrativeUnitSurveyId}`
    );
  }

  getAdministrativeUnitSurvey(administrativeUnitSurveyId: string) {
    return this.http.get<TenantSurvey>(
      `${this.baseUrl}/tenantsurvey/${administrativeUnitSurveyId}`
    );
  }

  getAdministrativeUnitSurveyStateAndAu(administrativeUnitSurveyId: string) {
    return this.http.get<TenantSurvey>(
      `${this.baseUrl}/tenantsurveystateandau/${administrativeUnitSurveyId}`
    );
  }

  update(survey: Survey): Observable<Survey> {
    return this.http.put<Survey>(`${this.baseUrl}/${survey?.id}`, survey);
  }

  add(survey: Survey): Observable<Survey> {
    return this.http.post<Survey>(`${this.baseUrl}`, survey);
  }

  clone(clone: CloneSurvey): Observable<Survey> {
    return this.http.post<Survey>(`${this.baseUrl}/clonesurvey`, clone);
  }

  testCloneMultiple(surveyIds: any[], schoolYearId: string): Observable<void> {
    return this.http.post<void>(
      `${this.baseUrl}/testfordupesurveycodesinschoolyear`,
      {
        surveyIds,
        schoolYearId,
      }
    );
  }

  cloneMultiple(surveyIds: any[], schoolYearId: string): Observable<Survey[]> {
    return this.http.post<Survey[]>(`${this.baseUrl}/clonemultisurvey`, {
      surveyIds,
      schoolYearId,
    });
  }

  translate(req: TranslateSurvey): Observable<Survey> {
    return this.http.post<Survey>(`${this.baseUrl}/translatesurvey`, req);
  }

  delete(surveyId: string): Observable<void> {
    return this.http.delete<void>(`${this.baseUrl}/${surveyId}`);
  }

  deleteLanguage(surveyId: string, languageId: string): Observable<void> {
    return this.http.delete<void>(
      `${this.baseUrl}/language/${surveyId}/${languageId}`
    );
  }

  saveAnswer(answer: SurveyAnswerModel): Observable<SurveyAnswerModel> {
    const headerDict = {
      "no-spinner": "stops spinner from showing",
    };

    const requestOptions = {
      headers: new HttpHeaders(headerDict),
    };

    return this.http.post<SurveyAnswerModel>(
      `${this.baseUrl}/saveanswer`,
      answer,
      requestOptions
    );
  }

  saveAnswerStateAndAu(
    answer: SurveyAnswerModel
  ): Observable<SurveyAnswerModel> {
    const headerDict = {
      "no-spinner": "stops spinner from showing",
    };

    const requestOptions = {
      headers: new HttpHeaders(headerDict),
    };

    return this.http.post<SurveyAnswerModel>(
      `${this.baseUrl}/saveanswerstateandau`,
      answer,
      requestOptions
    );
  }

  saveQuestionMessage(
    message: SaveAdministrativeUnitSurveyMessage
  ): Observable<AdministrativeUnitSurveyMessage> {
    const headerDict = {
      "no-spinner": "stops spinner from showing",
    };

    const requestOptions = {
      headers: new HttpHeaders(headerDict),
    };

    return this.http.post<AdministrativeUnitSurveyMessage>(
      `${this.baseUrl}/savequestionmessage`,
      message,
      requestOptions
    );
  }

  saveReviewResponse(
    req: SaveReviewResponse,
    hideSpinner: boolean = false
  ): Observable<SurveyAnswerModel> {
    const headerDict = {
      "no-spinner": "stops spinner from showing",
    };

    const requestOptions = {
      headers: hideSpinner ? new HttpHeaders(headerDict) : new HttpHeaders(),
    };

    return this.http.post<SurveyAnswerModel>(
      `${this.baseUrl}/savereviewresponse`,
      req,
      requestOptions
    );
  }

  getLanguages() {
    return this.http.get<Language[]>(`${this.baseUrl}/languages`);
  }
}
